// Правила игры: страницы про её механики и общие правила игровых автоматов.
//
// Механик у игры четыре, и каждая занимает свою страницу с иллюстрацией: Nudge Respin,
// заряд манометров, бонус мини-слота и Red Hot Spin. Текст к ним игра несёт свой,
// см. i18n/text.ts: правила фич переводчикам не отдавали.
//
// Общие правила пришли готовыми в паках перевода, ключами rules_title_N и rules_text_N.
// Разделов там двенадцать, игра берёт девять: про автоспин, про лобби и про Free Spins
// в этой игре читать нечего, таких кнопок и такой механики в ней нет, см. ADR-41.
//
// Раздел — это страница: заголовок, завиток под ним и абзацы стопкой. Длину строки перевода
// игра не знает, поэтому абзац переносится сам, а следующий встаёт под ним по его высоте.

import { Container, Text } from 'pixi.js';

import { BONUS_IMAGE, CHARGE_IMAGE, RESPIN_IMAGE, SCATTER_IMAGE, TITLE_FONT } from '../assets/catalog';
import type { AssetManager } from '../assets/manager';
import { text, type PhraseId } from '../i18n/text';

import { fitLabel } from './label';
import { pageBody, pageHeading, textStyle, CONTENT_Y, PAGE_BOTTOM, PAGE_WIDTH } from './popup';

/** Раздел общих правил: заголовок и абзацы под ним. */
interface Section {
  readonly title: PhraseId;
  readonly lines: readonly PhraseId[];
}

/** Страница про механику игры: заголовок, иллюстрация и правило под ней. */
interface Feature {
  readonly title: PhraseId;
  readonly picture: string;
  readonly rule: PhraseId;
}

/**
 * Механики игры по порядку, в котором игрок их встречает.
 *
 * Nudge Respin идёт с любого wild, заряд манометров — со стадии 2 фичи Steam Spin, бонус —
 * с полных манометров, Red Hot Spin — с трёх скаттеров. См. GDD, разделы 4.3, 5 и 6.
 */
const FEATURES: readonly Feature[] = [
  { title: 'nudgeRespin', picture: RESPIN_IMAGE, rule: 'nudgeRespinRules' },
  { title: 'steamSpinName', picture: CHARGE_IMAGE, rule: 'steamSpinRules' },
  { title: 'steamSpinName', picture: BONUS_IMAGE, rule: 'steamSpinBonusRules' },
  { title: 'redHotSpinName', picture: SCATTER_IMAGE, rule: 'redHotSpinRules' },
];

/**
 * Разделы общих правил.
 *
 * Строку про скаттер игра дописывает к разделу об особых символах сама: в паках её нет,
 * там переведена только строка про wild.
 */
const SECTIONS: readonly Section[] = [
  { title: 'aboutTitle', lines: ['aboutGame', 'aboutReels'] },
  { title: 'basicsTitle', lines: ['basicsFields', 'basicsBalance', 'basicsStake', 'basicsLastWin'] },
  { title: 'payoutsTitle', lines: ['payoutsText'] },
  { title: 'linesTitle', lines: ['linesText', 'linesActive'] },
  { title: 'betTitle', lines: ['betText'] },
  { title: 'playTitle', lines: ['playText', 'playWinLabel'] },
  { title: 'specialTitle', lines: ['specialText', 'specialWild', 'scatterRules', 'specialHelp'] },
  { title: 'helpTitle', lines: ['helpText'] },
  { title: 'rtpTitle', lines: ['rtpText'] },
];

/** Промежуток между абзацами. */
const PARAGRAPH_GAP: number = 16;

/** Насколько стопка абзацев может отойти от верха страницы, когда раздел короткий. */
const SECTION_DROP: number = 40;

/** Страница про одну механику: иллюстрация под заголовком и правило внизу страницы. */
function featurePage(feature: Feature, family: string, assets: AssetManager): Container {
  const page = new Container();

  page.addChild(pageHeading(text(feature.title), family, assets), pageBody(assets.texture(feature.picture), text(feature.rule), family));

  return page;
}

/**
 * Страница одного раздела общих правил: абзацы идут стопкой сверху вниз.
 *
 * Раздел длиннее страницы ужимается целиком: греческий перевод правил о ставке в полтора раза
 * длиннее английского, и абзацы иначе ушли бы под нижнюю рамку доски. Стопка стоит серединой
 * на середине страницы, поэтому ужатая она остаётся на месте.
 *
 * Стопка чуть опускается, когда раздел короткий: у верхнего края под завитком заголовка
 * два абзаца висели бы в пустоте. Глубже SECTION_DROP она не уходит, иначе длинный раздел
 * упёрся бы в низ доски.
 */
function sectionPage(section: Section, family: string, assets: AssetManager): Container {
  const page = new Container();
  const block = new Container();

  // Абзацы стоят на одном левом краю: среди них есть строки списка, и вразнобой они не читаются.
  let height = 0;

  for (const line of section.lines) {
    const paragraph = new Text({ text: text(line), style: { ...textStyle(family), align: 'left' } });
    paragraph.anchor.set(0, 0);
    paragraph.position.set(-PAGE_WIDTH / 2, height);

    block.addChild(paragraph);
    height += paragraph.height + PARAGRAPH_GAP;
  }

  // Промежуток за последним абзацем места не занимает: под ним уже ничего не стоит.
  const stack: number = height - PARAGRAPH_GAP;
  const room: number = PAGE_BOTTOM - CONTENT_Y;
  const fit: number = stack > 0 ? Math.min(room / stack, 1) : 1;

  block.scale.set(fit);
  block.y = CONTENT_Y + Math.min(SECTION_DROP, Math.max(0, (room - stack * fit) / 2));

  page.addChild(pageHeading(text(section.title), family, assets), block);

  return page;
}

/** Страницы правил игры: сперва механики, за ними общие правила. */
export function rulesPages(assets: AssetManager): Container[] {
  const family: string = assets.font(TITLE_FONT);

  return [
    ...FEATURES.map((feature: Feature) => featurePage(feature, family, assets)),
    ...SECTIONS.map((section: Section) => sectionPage(section, family, assets)),
  ];
}

/** Переписывает правила и пересчитывает высоту разделов, не пересобирая их страницы. */
export function refreshRulesPages(pages: readonly Container[]): void {
  for (let index = 0; index < FEATURES.length; index++) {
    const page = pages[index];
    const heading = (page.children[0] as Container).children[0] as Text;
    const note = (page.children[1] as Container).children[1] as Text;

    heading.text = text(FEATURES[index].title);
    fitLabel(heading, { width: PAGE_WIDTH });
    note.text = text(FEATURES[index].rule);
    fitLabel(note, { width: PAGE_WIDTH });
  }

  for (let index = 0; index < SECTIONS.length; index++) {
    const page = pages[FEATURES.length + index];
    const heading = (page.children[0] as Container).children[0] as Text;
    const block = page.children[1] as Container;
    const section = SECTIONS[index];
    let height = 0;

    heading.text = text(section.title);
    fitLabel(heading, { width: PAGE_WIDTH });

    for (let line = 0; line < section.lines.length; line++) {
      const paragraph = block.children[line] as Text;
      paragraph.text = text(section.lines[line]);
      paragraph.position.y = height;
      height += paragraph.height + PARAGRAPH_GAP;
    }

    const stack: number = height - PARAGRAPH_GAP;
    const room: number = PAGE_BOTTOM - CONTENT_Y;
    const fit: number = stack > 0 ? Math.min(room / stack, 1) : 1;
    block.scale.set(fit);
    block.y = CONTENT_Y + Math.min(SECTION_DROP, Math.max(0, (room - stack * fit) / 2));
  }
}

/** Все надписи, которые книжка правил берёт у перевода. По этому списку её сверяет тест. */
export function rulesPhrases(): PhraseId[] {
  return [
    ...FEATURES.flatMap((feature: Feature) => [feature.title, feature.rule]),
    ...SECTIONS.flatMap((section: Section) => [section.title, ...section.lines]),
  ];
}
